// src/edu-world/main.ts
// EduWorld entry point — wires all Phase 1 + Phase 2 systems for the vertical slice.
// Proves: Parque → movement → Rosa → proximity → dialogue → mission → minigame → result → flowers bloom
// Phase 2 adds: persistence, audio narration, micro-interactions, soft guide, feedback effects

import Phaser from 'phaser';
import { EventBus } from './core/event-bus';
import { WorldEngine } from './core/world-engine';
import { CountingMinigame } from './adapters/minigame-adapter';
import { loadVerticalSliceFromObjects } from './core/config-loader';
import type { PlayerConfig, CameraConfig, Mission, MissionState, MinigameInput } from './types';

// ── Import content JSON (Vite resolves these at build time) ──
import parqueZone from './content/zones/parque.json';
import rosaNpc from './content/npcs/rosa.json';
import tomeNpc from './content/npcs/tome.json';
import professorNpc from './content/npcs/professor.json';

// ── EduWorld Standalone missions ──
import rosaHortaMission from './content/missions/rosa-horta-numeros.json';
import tomeLetrasMission from './content/missions/tome-letras-parque.json';
import rosaBichosMission from './content/missions/rosa-bichos-padroes.json';
import standaloneHorta from './content/minigames/standalone-horta-numeros.json';
import standaloneLetras from './content/minigames/standalone-letras-parque.json';
import standaloneBichos from './content/minigames/standalone-bichos-padroes.json';
import hortaImpact from './content/impacts/impact-horta-legumes.json';
import letrasImpact from './content/impacts/impact-letras-arvore.json';
import bichosImpact from './content/impacts/impact-bichos-jardim.json';
import floresImpact from './content/impacts/impact-flores-bloom.json';

// ── Phase 2 module imports ──
import { loadOnBoot, registerEmergencySave, updateEmergencyState, saveOnSessionEnd } from './data/session-saver';
import { playNarrationWithStop } from './systems/audio-narrator';
import { showVisualFallback } from './systems/visual-fallback';
import { MicroInteractionManager, createPondSplashZone, createBenchButterflyZone, createFlowerLadybugZone } from './systems/micro-interactions';
import { SoftGuide } from './systems/soft-guide';
import { showPositiveFeedback } from './ui/feedback-effects';

// ── Phase 3 module imports ──
import { NPCRelationshipSystem } from './systems/npc-relationship';
import { SessionManager } from './systems/session-manager';
import { FarewellCueSystem } from './systems/farewell-cues';
import { ProgressionSystem } from './systems/progression-system';
import { wireProgressionToResults } from './systems/progression-wiring';
import { MissionDirectionIcon } from './systems/mission-direction';
import { WarmWelcome } from './systems/warm-welcome';
import { PathTrail } from './systems/path-trail';

// ── EduWorld Application ──

export class EduWorldApp {
  private game: Phaser.Game | null = null;
  private eventBus: EventBus;

  constructor() {
    this.eventBus = new EventBus();
  }

  start(parentElement?: string | HTMLElement): void {
    // Load and validate content
    const content = loadVerticalSliceFromObjects({
      zone: parqueZone,
      npcs: [rosaNpc, tomeNpc, professorNpc],
      missions: [rosaHortaMission, tomeLetrasMission, rosaBichosMission],
      minigames: [standaloneHorta, standaloneLetras, standaloneBichos],
      impacts: [floresImpact, hortaImpact, letrasImpact, bichosImpact],
    });

    // Player config (placeholder)
    const playerConfig: PlayerConfig = {
      speed: 120,
      sprite: 'placeholder-player',
      width: 36,
      height: 48,
    };

    // Camera config
    const cameraConfig: CameraConfig = {
      deadzone: 0.4,
      lerp: 0.08,
      maxDeltaFraction: 0.1,
    };

    // ── Create Phaser game ──
    const gameConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: window.innerWidth,
      height: window.innerHeight,
      parent: parentElement || 'edu-world-container',
      backgroundColor: '#2e7d32',
      scene: [WorldEngine],
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false,
        },
      },
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      input: {
        touch: { target: undefined },
      },
    };

    this.game = new Phaser.Game(gameConfig);

    // Add non-auto-start scenes
    this.game.scene.add('CountingMinigame', CountingMinigame, false);
    this.game.scene.add('EduWorldOrchestrator', EduWorldOrchestrator, false);
    this.game.scene.add('UIOverlay', UIOverlayScene, true); // auto-start, always on top

    // Phaser auto-starts the first scene in the array (WorldEngine).
    // We pass data via the scene's registry so the orchestrator can read it after WorldEngine creates.
    this.game.registry.set('edu-world-config', {
      zone: content.zone,
      player: playerConfig,
      camera: cameraConfig,
      eventBus: this.eventBus,
      content,
    });
  }

  destroy(): void {
    this.eventBus.clear();
    this.game?.destroy(true);
    this.game = null;
  }
}


// ── Orchestrator Scene ──
// Coordinates mission flow, NPC interaction, dialogue, minigame launch, results.
// Phase 2: Also manages persistence, audio, micro-interactions, soft guide, feedback effects.

import { NPCRenderer } from './systems/npc-renderer';
import { NPCInteraction } from './systems/npc-interaction';
import { DialogueBox, UIOverlayScene } from './ui/dialogue-box';
import { MinigameLauncher } from './systems/minigame-launcher';
import { StandaloneLauncher } from './systems/standalone-launcher';
import { ResultHandler } from './systems/result-handler';
import { WorldImpactRenderer } from './systems/world-impact-renderer';
import type { VerticalSliceContent } from './core/config-loader';
import type { NPCConfig, WorldState } from './types';

interface OrchestratorData {
  eventBus: EventBus;
  content: VerticalSliceContent;
  playerConfig: PlayerConfig;
}

class EduWorldOrchestrator extends Phaser.Scene {
  private eventBus!: EventBus;
  private content!: VerticalSliceContent;
  private npcRenderer!: NPCRenderer;
  private npcInteraction!: NPCInteraction;
  private dialogueBox!: DialogueBox;
  private minigameLauncher!: MinigameLauncher;
  private standaloneLauncher!: StandaloneLauncher;
  private resultHandler!: ResultHandler;
  private worldImpactRenderer!: WorldImpactRenderer;
  private playerController!: import('./core/player-controller').PlayerController;

  // Mission state
  private activeMission: Mission | null = null;

  // ── Phase 2 managers ──
  private microInteractionManager!: MicroInteractionManager;
  private softGuide!: SoftGuide;

  // ── Phase 3 managers ──
  private npcRelationshipSystem!: NPCRelationshipSystem;
  private sessionManager!: SessionManager;
  private farewellCueSystem!: FarewellCueSystem;
  private progressionSystem!: ProgressionSystem;
  private missionDirectionIcon!: MissionDirectionIcon;
  private pathTrail!: PathTrail;
  private farewellCueShownThisSession: boolean = false;

  constructor() {
    super({ key: 'EduWorldOrchestrator' });
  }

  init(data: OrchestratorData): void {
    this.eventBus = data.eventBus;
    this.content = data.content;
  }

  create(): void {
    const worldEngine = this.scene.get('WorldEngine') as WorldEngine;
    const playerController = worldEngine.getPlayerController();
    this.playerController = playerController;

    // ── Phase 2: Persistence — load state on boot ──
    this.initPersistence();

    // ── NPC Rendering ──
    this.npcRenderer = new NPCRenderer(worldEngine);
    for (const npc of this.content.npcs) {
      const hasMission = this.content.missions.some(
        (m) => m.npc_id === npc.id && m.state === 'available'
      );
      this.npcRenderer.addNPC(npc, hasMission);
    }

    // ── Register Orchestrator NPCs in PlacementEditor ──
    const placementEditor = this.scene.get('WorldEngine')?.game?.registry?.get('placementEditor');
    if (placementEditor) {
      for (const npcSprite of this.npcRenderer.getAllNPCs()) {
        placementEditor.register(npcSprite.body, npcSprite.id, 'npc');
      }
    }

    // ── NPC Interaction (proximity detection) ──
    this.npcInteraction = new NPCInteraction(
      worldEngine,
      this.eventBus,
      this.npcRenderer,
      playerController,
      this.content.npcs
    );

    // ── Dialogue Box (on UIOverlay scene for reliable click detection) ──
    const uiScene = this.scene.get('UIOverlay');
    this.dialogueBox = new DialogueBox(uiScene || worldEngine);

    // ── Minigame Launcher ──
    this.minigameLauncher = new MinigameLauncher(worldEngine, this.eventBus);

    // ── Standalone Launcher (for iframe-based standalone adventures) ──
    this.standaloneLauncher = new StandaloneLauncher(this.eventBus);

    // ── World Impact Renderer ──
    this.worldImpactRenderer = new WorldImpactRenderer(
      worldEngine,
      this.eventBus,
      this.content.impacts
    );

    // ── Result Handler ──
    this.resultHandler = new ResultHandler({
      eventBus: this.eventBus,
      npcRenderer: this.npcRenderer,
      dialogueBox: this.dialogueBox,
      missions: this.content.missions,
      impacts: this.content.impacts,
      onMissionStateChange: (id, state) => this.handleMissionStateChange(id, state),
      onMissionFailure: (missionId, npcId) => this.handleMissionFailure(missionId, npcId),
    });

    // ── Phase 2: Micro-Interactions ──
    this.initMicroInteractions(worldEngine);

    // ── Phase 2: Soft Guide ──
    this.initSoftGuide(worldEngine);

    // ── Phase 3: NPC Relationship System ──
    this.npcRelationshipSystem = new NPCRelationshipSystem();

    // ── Phase 3: Session Manager ──
    this.sessionManager = new SessionManager();
    this.sessionManager.startSession();

    // ── Phase 3: Farewell Cues ──
    this.farewellCueSystem = new FarewellCueSystem();
    this.farewellCueShownThisSession = false;

    // ── Phase 3: Progression System ──
    this.progressionSystem = new ProgressionSystem();
    wireProgressionToResults(this.progressionSystem, this.eventBus);

    // ── Phase 3: Mission Direction Icon ──
    this.missionDirectionIcon = new MissionDirectionIcon(worldEngine);

    // ── Phase 3: Path Trail (dotted breadcrumb to mission target) ──
    this.pathTrail = new PathTrail(worldEngine);
    // Show trail from start — guide player toward Rosa
    const rosaForTrail = this.content.npcs.find((n) => n.id === 'rosa');
    if (rosaForTrail) {
      this.pathTrail.show(rosaForTrail.position);
    }

    // ── Phase 3: Warm Welcome ──
    const warmWelcome = new WarmWelcome();
    const welcomeConfig = warmWelcome.getWelcomeConfig(1, null);
    // Ensure Rosa (or selected NPC) is highlighted from the start
    const highlightedNpc = this.content.npcs.find((n) => n.id === welcomeConfig.npcToHighlight);
    if (highlightedNpc) {
      this.npcRenderer.playBeckonAnimation(highlightedNpc.id);
    }

    // ── Wire events ──
    this.wireEvents();
  }

  update(): void {
    // Update proximity detection every frame
    this.npcInteraction.update();

    // Phase 2: Update soft guide every frame
    this.softGuide.update();

    // Phase 3: Update mission direction icon every frame
    const playerPos = this.playerController.getPosition();
    const isMoving = this.playerController.getIsMoving?.() ?? false;
    this.missionDirectionIcon.update(playerPos, isMoving);

    // Phase 3: Update path trail every frame
    this.pathTrail.update(playerPos);

    // Phase 3: Check farewell cues (only show once per session)
    if (!this.farewellCueShownThisSession) {
      const sessState = {
        missionsCompleted: this.sessionManager.getSessionState()?.missionsCompleted ?? 0,
        elapsedMs: this.sessionManager.getElapsedMs(),
        phase: this.sessionManager.getSessionPhase(),
      };

      const windingDownCue = this.farewellCueSystem.checkWindingDown(sessState);
      if (windingDownCue && windingDownCue.dialogue) {
        // Show a gentle winding-down dialogue via dialogueBox (only once)
        this.farewellCueShownThisSession = true;
        this.dialogueBox.show({
          npcName: 'Rosa',
          text: windingDownCue.dialogue,
        });
        // Auto-hide after 3 seconds — it's just a gentle suggestion
        this.time.delayedCall(3000, () => {
          this.dialogueBox.hide();
        });
      } else if (this.farewellCueSystem.shouldShowFarewell(sessState)) {
        // Show farewell suggestion (only once)
        this.farewellCueShownThisSession = true;
        const farewell = this.farewellCueSystem.createFarewellMoment();
        this.dialogueBox.show({
          npcName: farewell.farewellNpcName,
          text: farewell.farewellDialogue,
        });
        this.time.delayedCall(farewell.maxDurationMs, () => {
          this.dialogueBox.hide();
        });
      }
    }
  }

  // ─── Phase 2: Persistence ───

  private initPersistence(): void {
    // Load saved state on boot (async, non-blocking)
    loadOnBoot().then((savedState) => {
      // Apply restored state to mission/impact data
      this.applyRestoredState(savedState);
    }).catch(() => {
      // Silently ignore — persistence is optional
      console.warn('[Orchestrator] Persistence load failed, using default state');
    });

    // Register emergency save with current state getter
    registerEmergencySave(() => this.getCurrentWorldState());
  }

  private applyRestoredState(state: WorldState): void {
    // Restore mission states
    for (const mission of this.content.missions) {
      const savedState = state.missions[mission.id];
      if (savedState) {
        if (savedState === 'active') {
          // If a mission was left in 'active' state (user closed before completing),
          // reset it to 'available' so the player can accept it again.
          mission.state = 'available';
        } else {
          mission.state = savedState;
        }
      }
    }

    // Never restore activeMission — the player must re-accept from the NPC.
    this.activeMission = null;

    // Restore applied world impacts
    if (state.impacts_applied && state.impacts_applied.length > 0) {
      // Emit world impact events for previously applied impacts
      for (const impactId of state.impacts_applied) {
        const impact = this.content.impacts.find((i) => i.id === impactId);
        if (impact) {
          // Find the mission associated with this impact for the event payload
          const relatedMission = this.content.missions.find(
            (m) => m.world_impact_ref === impactId
          );
          this.eventBus.emit('world:impact', {
            impact_id: impactId,
            mission_id: relatedMission?.id ?? '',
          });
        }
      }
    }
  }

  private getCurrentWorldState(): WorldState {
    const missions: Record<string, import('./types').MissionState> = {};
    for (const m of this.content.missions) {
      missions[m.id] = m.state;
    }

    const impactsApplied = this.content.impacts
      .filter((i) => this.content.missions.some(
        (m) => m.world_impact_ref === i.id && m.state === 'completed'
      ))
      .map((i) => i.id);

    const pos = this.playerController.getPosition();

    return {
      missions,
      impacts_applied: impactsApplied,
      player_position: { x: pos.x, y: pos.y },
      current_zone: this.content.zone.id,
    };
  }

  // ─── Phase 2: Micro-Interactions ───

  private initMicroInteractions(worldEngine: WorldEngine): void {
    this.microInteractionManager = new MicroInteractionManager(worldEngine);

    // Register the 3 micro-interaction zones
    this.microInteractionManager.registerZone(createPondSplashZone());
    this.microInteractionManager.registerZone(createBenchButterflyZone());
    this.microInteractionManager.registerZone(createFlowerLadybugZone());

    // Set tap interceptor on player controller — intercept taps on micro-interaction zones
    this.playerController.setTapInterceptor((worldX: number, worldY: number) => {
      const triggered = this.microInteractionManager.tryInteraction(worldX, worldY);
      if (triggered) {
        // Show feedback effect for micro-interaction
        showPositiveFeedback(worldEngine, { x: worldX, y: worldY }, 'micro_interact');
        // Record interaction for soft guide
        this.softGuide.recordInteraction();
        // Phase 3: Record interaction in session manager
        this.sessionManager.recordInteraction();
      }
      return triggered;
    });
  }

  // ─── Phase 2: Soft Guide ───

  private initSoftGuide(worldEngine: WorldEngine): void {
    const rosa = this.content.npcs.find((n) => n.id === 'rosa');
    const rosaPosition = rosa?.position ?? { x: 520, y: 380 };

    this.softGuide = new SoftGuide(
      worldEngine,
      () => this.playerController.getPosition(),
      rosaPosition,
      worldEngine.getZoneTransitionSystem()
    );
  }

  // ─── Event Wiring ───

  private wireEvents(): void {
    // Player activates NPC → show dialogue / mission request
    this.eventBus.on('player:activate', (event) => {
      this.handleNPCActivation(event.npc_id);
    });

    // Player leaves proximity → hide dialogue and unlock input
    this.eventBus.on('player:proximity', (event) => {
      if (!event.in_range && this.dialogueBox.getIsVisible()) {
        this.dialogueBox.hide();
        this.playerController.setInputLocked(false);
      }
    });

    // Mission accepted → launch minigame
    this.eventBus.on('mission:accepted', (event) => {
      this.handleMissionAccepted(event.mission_id, event.minigame_link);
    });
  }

  // ─── NPC Activation ───

  private handleNPCActivation(npcId: string): void {
    // Don't open dialogue during minigame
    if (this.minigameLauncher.getIsActive()) return;
    // Don't open dialogue if one is already showing
    if (this.dialogueBox.getIsVisible()) return;

    const npc = this.content.npcs.find((n) => n.id === npcId);
    if (!npc) return;

    // Lock player input during dialogue
    this.playerController.setInputLocked(true);

    // Phase 2: Feedback effect for NPC interaction
    const worldEngine = this.scene.get('WorldEngine') as WorldEngine;
    showPositiveFeedback(worldEngine, npc.position, 'npc_interact');

    // Phase 2: Record interaction for soft guide
    this.softGuide.recordInteraction();

    // Phase 3: Hide path trail — player found the NPC
    this.pathTrail.hide();

    // Phase 3: Record NPC relationship interaction (greeting visit)
    this.npcRelationshipSystem.recordInteraction(npcId, 'greeting_visit');
    this.npcRelationshipSystem.recordInteraction(npcId, 'greeting_visit');

    // Phase 3: Record interaction in session manager
    this.sessionManager.recordInteraction();

    // Check if this NPC has an available mission
    const mission = this.content.missions.find(
      (m) => m.npc_id === npcId && m.state === 'available'
    );

    if (mission && !this.activeMission) {
      // Show greeting then mission request
      this.showMissionDialogue(npc, mission);
    } else if (this.activeMission && this.activeMission.npc_id === npcId) {
      // Player is interacting with the NPC whose mission is active — re-launch the game
      this.dialogueBox.show({
        npcName: npc.name,
        text: npc.dialogues.mission_request || this.activeMission.context_text,
        showMissionOptions: true,
        onAccept: () => {
          this.playerController.setInputLocked(false);
          this.eventBus.emit('mission:accepted', {
            mission_id: this.activeMission!.id,
            npc_id: npcId,
            minigame_link: this.activeMission!.minigame_link,
          });
        },
        onDecline: () => {
          this.playerController.setInputLocked(false);
        },
      });
    } else if (this.activeMission) {
      // Already have an active mission with a different NPC — gentle indication
      this.dialogueBox.show({
        npcName: npc.name,
        text: npc.dialogues.ambient || 'Boa sorte!',
      });
      // Auto-hide after 2s
      this.time.delayedCall(2000, () => {
        this.dialogueBox.hide();
        this.playerController.setInputLocked(false);
      });
    } else {
      // No mission available — show ambient dialogue
      this.dialogueBox.show({
        npcName: npc.name,
        text: npc.dialogues.ambient || npc.dialogues.greeting,
      });
      // Auto-hide after 2s
      this.time.delayedCall(2000, () => {
        this.dialogueBox.hide();
        this.playerController.setInputLocked(false);
      });
    }
  }

  private showMissionDialogue(npc: NPCConfig, mission: Mission): void {
    // Step 1: Greeting
    this.dialogueBox.show({
      npcName: npc.name,
      text: npc.dialogues.greeting,
    });

    // Phase 2: Audio narration for greeting
    if (npc.audio?.greeting) {
      playNarrationWithStop(npc.audio.greeting).catch(() => {
        // Audio failed gracefully — visual fallback can be triggered in a future iteration
      });
    }

    // Step 2: After brief pause, show mission request with accept/decline
    this.time.delayedCall(1500, () => {
      this.dialogueBox.show({
        npcName: npc.name,
        text: npc.dialogues.mission_request || mission.context_text,
        showMissionOptions: true,
        onAccept: () => {
          // Accept mission
          mission.state = 'active';
          this.activeMission = mission;
          this.npcRenderer.playAcceptAnimation(npc.id);
          this.npcRenderer.hideMissionIndicator(npc.id);
          this.playerController.setInputLocked(false);

          // Phase 2: Feedback effect for mission accept (subtle sparkle only)
          const worldEngine = this.scene.get('WorldEngine') as WorldEngine;
          showPositiveFeedback(worldEngine, npc.position, 'npc_interact');

          // Phase 2: Record interaction for soft guide
          this.softGuide.recordInteraction();

          // Phase 2: Update emergency state on mission state change
          updateEmergencyState(this.getCurrentWorldState());

          // Phase 3: Show mission direction icon
          this.missionDirectionIcon.show(npc.position);

          // Phase 3: Show path trail to mission target
          this.pathTrail.show(npc.position);

          this.eventBus.emit('mission:accepted', {
            mission_id: mission.id,
            npc_id: npc.id,
            minigame_link: mission.minigame_link,
          });
        },
        onDecline: () => {
          // Decline — keep available, neutral animation
          this.npcRenderer.playBeckonAnimation(npc.id);
          this.playerController.setInputLocked(false);
        },
      });

      // Phase 2: Audio narration for mission request
      if (npc.audio?.mission_request) {
        playNarrationWithStop(npc.audio.mission_request).catch(() => {
          // Audio failed gracefully
        });
      }
    });
  }

  // ─── Mission Flow ───

  private handleMissionAccepted(missionId: string, minigameLink: string): void {
    const minigameConfig = this.content.minigames.find((mg) => mg.id === minigameLink);
    if (!minigameConfig) {
      console.error(`[Orchestrator] Minigame config not found: ${minigameLink}`);
      return;
    }

    const worldEngine = this.scene.get('WorldEngine') as WorldEngine;
    const playerPos = worldEngine.getPlayerController().getPosition();

    const input: MinigameInput = {
      mission_id: missionId,
      mechanic_type: minigameConfig.mechanic_type,
      difficulty: minigameConfig.difficulty,
      content_type: minigameConfig.content_type,
      config: minigameConfig,
    };

    // Check if this is a standalone adventure (iframe-based)
    if (StandaloneLauncher.isStandalone(input)) {
      this.time.delayedCall(800, () => {
        this.standaloneLauncher.launch(input);
      });
    } else {
      // Launch embedded minigame after brief delay (letting accept animation play)
      this.time.delayedCall(800, () => {
        this.minigameLauncher.launch(input, playerPos);
      });
    }
  }

  private handleMissionStateChange(missionId: string, state: MissionState): void {
    if (state === 'completed') {
      this.activeMission = null;

      // Phase 3: Hide mission direction icon on completion
      this.missionDirectionIcon.hide();

      // Phase 3: Hide path trail on completion
      this.pathTrail.hide();

      // Phase 2: Audio narration for success (gratitude)
      const mission = this.content.missions.find((m) => m.id === missionId);
      if (mission) {
        const npc = this.content.npcs.find((n) => n.id === mission.npc_id);
        if (npc?.audio?.gratitude) {
          playNarrationWithStop(npc.audio.gratitude).catch(() => {
            // Audio failed gracefully
          });
        }

        // Phase 2: Feedback effect for mission complete
        const worldEngine = this.scene.get('WorldEngine') as WorldEngine;
        showPositiveFeedback(worldEngine, npc?.position ?? { x: 520, y: 380 }, 'mission_complete');

        // Phase 3: Record mission_completed in NPC relationship system
        if (mission.npc_id) {
          this.npcRelationshipSystem.recordInteraction(mission.npc_id, 'mission_completed');
        }
      }

      // Phase 3: Record mission completion in session manager
      this.sessionManager.recordMissionComplete();

      // Phase 2: Persist state on mission completion
      saveOnSessionEnd(this.getCurrentWorldState()).catch(() => {
        console.warn('[Orchestrator] Save on mission complete failed');
      });
    }

    // Phase 2: Update emergency state on any mission state change
    updateEmergencyState(this.getCurrentWorldState());
  }

  private handleMissionFailure(_missionId: string, npcId: string): void {
    // Phase 2: Audio narration for encouragement on failure
    const npc = this.content.npcs.find((n) => n.id === npcId);
    if (npc?.audio?.encouragement) {
      playNarrationWithStop(npc.audio.encouragement).catch(() => {
        // Audio failed gracefully
      });
    }
  }
}

// ── Auto-start if running standalone ──
if (typeof document !== 'undefined') {
  // Create container div if it doesn't exist
  if (!document.getElementById('edu-world-container')) {
    const container = document.createElement('div');
    container.id = 'edu-world-container';
    container.style.width = '100%';
    container.style.height = '100vh';
    container.style.margin = '0';
    container.style.padding = '0';
    document.body.appendChild(container);
  }

  const app = new EduWorldApp();
  app.start('edu-world-container');
}
